import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, Download, AlertTriangle, CheckCircle, Eye } from 'lucide-react';

/**
 * Agent-portal "Regulatory Forms" tab.
 *
 * Shows the 5 annual per-advisor compliance PDFs (Advisor Disclosure, Privacy/CASL,
 * Complaint Handling, AML/ATF, Needs-Based Sales) with:
 *   - A soft-reminder banner when any form will expire within 60 days
 *   - Per-form download + preview-in-new-tab buttons
 *   - The revision stamp on every card
 *
 * Each form is auto-prefilled from the advisor's profile (name, license, email,
 * carriers) at download time — there is no upload step here.
 */
export default function RegulatoryFormsTab({ agent, apiUrl }) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/agents/forms`);
        if (res.ok) setForms(await res.json());
      } catch (e) {
        console.error('Failed to load regulatory forms:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [apiUrl]);

  // Compute soft-reminder banner — fires when the earliest expiry is < 60d away
  const soonestExpiry = forms.reduce((min, f) => {
    const t = new Date(f.expires_at).getTime();
    return min == null || t < min ? t : min;
  }, null);
  const daysUntilExpiry = soonestExpiry ? Math.ceil((soonestExpiry - Date.now()) / 86_400_000) : null;
  const showBanner = daysUntilExpiry != null && daysUntilExpiry <= 60;

  const urlFor = (slug) => {
    const qs = clientName ? `?client_name=${encodeURIComponent(clientName)}` : '';
    return `${apiUrl}/api/agents/${agent.id}/forms/${slug}.pdf${qs}`;
  };

  return (
    <div className="space-y-6" data-testid="regulatory-forms-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#0077B6]" />
            Annual Regulatory Forms
          </CardTitle>
          <p className="text-sm text-gray-600 mt-2">
            These five forms are submitted to your provincial Insurance Council each year
            when you renew your license. They are pre-filled with your profile information
            and the insurance carriers you hold contracts with — review, sign, and submit.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* Soft renewal-reminder banner */}
          {showBanner && (
            <div
              className={`border-l-4 rounded-lg p-4 flex items-start gap-3 ${
                daysUntilExpiry <= 0
                  ? 'bg-red-50 border-red-500 text-red-900'
                  : 'bg-amber-50 border-amber-500 text-amber-900'
              }`}
              data-testid="renewal-banner"
            >
              {daysUntilExpiry <= 0 ? (
                <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              )}
              <div className="text-sm">
                <p className="font-semibold mb-1">
                  {daysUntilExpiry <= 0
                    ? 'Your annual regulatory forms have expired.'
                    : `Your annual regulatory forms expire in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}.`}
                </p>
                <p>
                  Download the latest revision below, sign, and submit to your provincial
                  Insurance Council before <b>{new Date(soonestExpiry).toLocaleDateString('en-CA')}</b>{' '}
                  to keep your license in good standing.
                </p>
              </div>
            </div>
          )}

          {/* Optional client-name field (for forms presented to a client during a sale) */}
          <div className="bg-gray-50 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3">
            <label className="text-sm text-gray-700 font-medium whitespace-nowrap">
              Optional — pre-fill client name on the form:
            </label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="e.g. Jane Doe"
              className="flex-1 px-3 py-1.5 border rounded text-sm focus:ring-2 focus:ring-[#0077B6]"
              data-testid="prefill-client-name"
            />
          </div>

          {loading && (
            <p className="text-sm text-gray-500 py-6 text-center">Loading forms…</p>
          )}

          {!loading && (
            <div className="grid gap-3 md:grid-cols-2" data-testid="forms-grid">
              {forms.map((f) => (
                <div
                  key={f.slug}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                  data-testid={`form-card-${f.slug}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 leading-tight">{f.name}</h3>
                      <p className="text-xs text-gray-500 mt-1">{f.revision_stamp}</p>
                    </div>
                    <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap">
                      <CheckCircle className="h-3 w-3" />
                      Pre-filled
                    </span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(urlFor(f.slug), '_blank')}
                      data-testid={`preview-${f.slug}`}
                      className="flex-1"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = urlFor(f.slug);
                        link.download = `utmost-${f.slug}-${(agent.name || '').replace(/\s+/g, '_').toLowerCase()}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      data-testid={`download-${f.slug}`}
                      className="flex-1 bg-[#0077B6]"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Expires: <b>{new Date(f.expires_at).toLocaleDateString('en-CA')}</b>
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-3 text-sm text-blue-900 mt-4">
            <p className="font-medium mb-1">💡 Tip — keeping your forms accurate</p>
            <p className="text-xs">
              The <b>Advisor Disclosure</b> form lists the insurance carriers you hold contracts with.
              To update that list, edit your profile under <b>My Carriers</b> — those changes are
              picked up the next time you download the form.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
